<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { api, type FundAccessRow, type FundAccessFund } from '../api/client'
import EntitlementGrantConfirm from './EntitlementGrantConfirm.vue'
import { ACCESS_GRID_FUNDS, isGatedFund, registryEntryForFund } from './nav/fundConfig'

// Spec 2026-09-30 per-user fund access §8: the administrator decides which funds each user may have.
// BGA/JH/GOLD/GGE → PUT /api/admin/users/{id}/fund-access/{fund}. A gated fund (JAPAN today) → the
// existing fund-entitlements grant/revoke, behind ONE inline confirmation (never a browser dialog).
// Each switch saves on change and shows "Saved" or the server's error inline (e.g. the 409
// "holds N units — sell first"). Phone widths get one card per user (CSS, ≥ 44px touch targets).
// PR 0 (spec 2026-10-05): columns and the gated/live split come from the fund registry, not a
// hand-written FUNDS literal + 'JAPAN' checks.
const FUNDS: ReadonlyArray<FundAccessFund> = ACCESS_GRID_FUNDS as ReadonlyArray<FundAccessFund>

const rows = ref<FundAccessRow[]>([])
const loading = ref(true)
const loadError = ref<string | null>(null)
const saving = ref<string | null>(null)
const cellStatus = ref<Record<string, { kind: 'saved' | 'error'; text: string }>>({})
const pendingGrant = ref<{ userId: string; fund: FundAccessFund } | null>(null)

const cellKey = (userId: string, fund: FundAccessFund) => `${userId}:${fund}`
const fundLabel = (fund: FundAccessFund) => registryEntryForFund(fund).label

async function load() {
  loading.value = true
  loadError.value = null
  try {
    rows.value = await api.adminFundAccess()
  } catch (e: any) {
    loadError.value = e.message ?? 'Failed to load fund access'
  } finally {
    loading.value = false
  }
}

function unitsLabel(units: number): string {
  return `holds ${units.toLocaleString('en-GB', { maximumFractionDigits: 4 })} units`
}

async function apply(row: FundAccessRow, fund: FundAccessFund, allowed: boolean, input: HTMLInputElement | null) {
  const k = cellKey(row.userId, fund)
  saving.value = k
  delete cellStatus.value[k]
  try {
    if (isGatedFund(fund)) {
      if (allowed) await api.adminGrantEntitlement(row.userId, fund)
      else await api.adminRevokeEntitlement(row.userId, fund)
    } else {
      await api.adminSetFundAccess(row.userId, fund, allowed)
    }
    if (row.funds[fund]) row.funds[fund].allowed = allowed
    cellStatus.value[k] = { kind: 'saved', text: 'Saved' }
  } catch (e: any) {
    // The bound value never changed, so put the clicked switch back by hand.
    if (input) input.checked = !allowed
    cellStatus.value[k] = { kind: 'error', text: e.message ?? 'Failed to save' }
  } finally {
    saving.value = null
  }
}

function onToggle(row: FundAccessRow, fund: FundAccessFund, evt: Event) {
  const input = evt.target as HTMLInputElement
  const allowed = input.checked
  if (isGatedFund(fund) && allowed) {
    input.checked = false   // not granted until the operator confirms
    pendingGrant.value = { userId: row.userId, fund }
    return
  }
  void apply(row, fund, allowed, input)
}

function confirmGrant(row: FundAccessRow) {
  const p = pendingGrant.value
  pendingGrant.value = null
  if (p) void apply(row, p.fund, true, null)
}

function cancelGrant() {
  pendingGrant.value = null
}

onMounted(load)
</script>

<template>
  <section class="fa" data-testid="fund-access">
    <p class="fa-intro">
      A fund switched off is hidden from that user, can't be armed and isn't dealt.
      A fund they still hold must be sold first.
    </p>
    <p v-if="loading" class="fa-muted">Loading…</p>
    <p v-else-if="loadError" class="fa-error" data-testid="fa-load-error">{{ loadError }}</p>
    <template v-else>
      <!-- Tablet / desktop: one row per user -->
      <table class="fa-table" data-testid="fa-table">
        <thead>
          <tr>
            <th scope="col">User</th>
            <th v-for="fund in FUNDS" :key="fund" scope="col">{{ fundLabel(fund) }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in rows" :key="row.userId">
            <tr :data-testid="`fa-row-${row.userId}`">
              <th scope="row" class="fa-user">
                <span class="fa-label">{{ row.label }}</span>
                <span :class="['fa-role', `fa-role-${row.role}`]">{{ row.role }}</span>
              </th>
              <td v-for="fund in FUNDS" :key="fund" class="fa-cell">
                <label class="fa-switch">
                  <input
                    type="checkbox"
                    role="switch"
                    :data-testid="`fa-toggle-${row.userId}-${fund}`"
                    :aria-label="`${fundLabel(fund)} for ${row.label}`"
                    :checked="row.funds[fund]?.allowed ?? false"
                    :disabled="saving === cellKey(row.userId, fund)"
                    @change="onToggle(row, fund, $event)"
                  />
                </label>
                <span v-if="(row.funds[fund]?.heldUnits ?? 0) > 0" class="fa-held"
                      :data-testid="`fa-held-${row.userId}-${fund}`">{{ unitsLabel(row.funds[fund]?.heldUnits ?? 0) }}</span>
                <span v-if="cellStatus[cellKey(row.userId, fund)]"
                      :class="['fa-status', `fa-status-${cellStatus[cellKey(row.userId, fund)].kind}`]"
                      :data-testid="`fa-status-${row.userId}-${fund}`">{{ cellStatus[cellKey(row.userId, fund)].text }}</span>
              </td>
            </tr>
            <tr v-if="pendingGrant?.userId === row.userId" class="fa-confirm-row">
              <td :colspan="FUNDS.length + 1">
                <EntitlementGrantConfirm
                  :label="row.label"
                  :fund-label="fundLabel(pendingGrant.fund)"
                  confirm-testid="fa-japan-confirm"
                  grant-testid="fa-japan-grant"
                  cancel-testid="fa-japan-cancel"
                  @grant="confirmGrant(row)"
                  @cancel="cancelGrant"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Phone: one card per user, five switches -->
      <div class="fa-cards" data-testid="fa-cards">
        <article v-for="row in rows" :key="row.userId" class="fa-card" :data-testid="`fa-card-${row.userId}`">
          <header class="fa-card-head">
            <span class="fa-label">{{ row.label }}</span>
            <span :class="['fa-role', `fa-role-${row.role}`]">{{ row.role }}</span>
          </header>
          <label v-for="fund in FUNDS" :key="fund" class="fa-switch fa-card-switch">
            <span class="fa-card-fund">{{ fundLabel(fund) }}</span>
            <span v-if="(row.funds[fund]?.heldUnits ?? 0) > 0" class="fa-held">{{ unitsLabel(row.funds[fund]?.heldUnits ?? 0) }}</span>
            <span v-if="cellStatus[cellKey(row.userId, fund)]"
                  :class="['fa-status', `fa-status-${cellStatus[cellKey(row.userId, fund)].kind}`]"
                  :data-testid="`fa-card-status-${row.userId}-${fund}`">{{ cellStatus[cellKey(row.userId, fund)].text }}</span>
            <input
              type="checkbox"
              role="switch"
              :data-testid="`fa-card-toggle-${row.userId}-${fund}`"
              :aria-label="`${fundLabel(fund)} for ${row.label}`"
              :checked="row.funds[fund]?.allowed ?? false"
              :disabled="saving === cellKey(row.userId, fund)"
              @change="onToggle(row, fund, $event)"
            />
          </label>
          <EntitlementGrantConfirm
            v-if="pendingGrant?.userId === row.userId"
            :label="row.label"
            :fund-label="fundLabel(pendingGrant.fund)"
            confirm-testid="fa-card-japan-confirm"
            grant-testid="fa-card-japan-grant"
            cancel-testid="fa-card-japan-cancel"
            @grant="confirmGrant(row)"
            @cancel="cancelGrant"
          />
        </article>
      </div>
    </template>
  </section>
</template>

<style scoped>
.fa { display: flex; flex-direction: column; gap: 0.75rem; color: #c9d1d9; }
.fa-intro, .fa-muted { color: #8b949e; margin: 0; }
.fa-error { color: #f85149; margin: 0; }
.fa-table { width: 100%; border-collapse: collapse; background: #161b22; border: 1px solid #21262d; border-radius: 8px; }
.fa-table th, .fa-table td { padding: 0.5rem; border-bottom: 1px solid #21262d; text-align: center; vertical-align: middle; }
.fa-table .fa-user { text-align: left; }
.fa-label { font-weight: 600; margin-right: 0.5rem; overflow-wrap: anywhere; }
.fa-role { font-size: 0.75rem; padding: 0.1rem 0.4rem; border-radius: 999px; border: 1px solid #30363d; }
.fa-role-superuser { color: #d29922; border-color: #d29922; }
.fa-role-viewer { color: #58a6ff; border-color: #58a6ff; }
.fa-switch { display: inline-flex; align-items: center; justify-content: center; min-width: 44px; min-height: 44px; cursor: pointer; }
.fa-switch input { width: 22px; height: 22px; cursor: pointer; }
.fa-held { display: block; font-size: 0.75rem; color: #d29922; }
.fa-status { display: block; font-size: 0.75rem; }
.fa-status-saved { color: #3fb950; }
.fa-status-error { color: #f85149; }
.fa-cards { display: none; }
.fa-card { background: #161b22; border: 1px solid #21262d; border-radius: 8px; padding: 0.75rem; }
.fa-card-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.25rem; }
.fa-card-switch { display: flex; justify-content: space-between; gap: 0.5rem; width: 100%; border-top: 1px solid #21262d; }
.fa-card-fund { flex: 1; text-align: left; }
@media (max-width: 640px) {
  .fa-table { display: none; }
  .fa-cards { display: flex; flex-direction: column; gap: 0.75rem; }
}
</style>
